import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { apiBaseUrl } from "../../constants/urls";
import { getToken } from "../../auth/AuthToken";
import { toast } from "react-toastify";

// Every consumer of state.user.role compares it as a string ("uber_admin",
// roles.includes(role), and so on), but a role can arrive here as the whole
// { account_id, role_name } object — from the selectAccount fallback below, or
// from a value already persisted in that shape. Coercing on the way in keeps
// one shape in the store instead of asking a dozen call sites to handle both.
const normalizeRole = (value) => {
  if (typeof value === "string") return value;
  return value?.role_name ?? null;
};

const initialState = {
  user: JSON.parse(localStorage.getItem("user")),
  role: normalizeRole(JSON.parse(localStorage.getItem("role"))),
  selectedAccount: JSON.parse(localStorage.getItem("selectedAccount")),
  currentUser: null,
  roleWiseUser: null,
  // { groupId, subjects } — whose workers are currently held, so a stale
  // response for a group the user has already moved off can be ignored.
  groupSubjects: null,
  groupSubjectsLoading: false,
  userStatus: null,
  usersList: [],
  pendingUsersList: [],
  totalPendingUsersCount: 0,
  isLoading: false,
  totalUsersCount: 0,
  adminUsersList: [],
  adminUsersMeta: null,
  adminUsersLoading: false,
  adminUserDetail: null,
  adminUserDetailLoading: false,
  adminUserDetailError: null,
  adminInviteRoles: [],
  adminPendingInvites: [],
};

export const loginUser = createAsyncThunk(
  "user/login",
  async (session, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/login";
    try {
      const response = await axios.post(url, session, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getTrialSignupGroups = createAsyncThunk(
  "user/getTrialSignupGroups",
  async (_, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/trial_signup/groups";
    try {
      const response = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to load groups" });
    }
  }
);

export const sendTrialSignupOtp = createAsyncThunk(
  "user/sendTrialSignupOtp",
  async (registration, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/trial_signup/send_otp";
    try {
      const response = await axios.post(
        url,
        { registration },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to send verification code" });
    }
  }
);

export const verifyTrialSignupOtp = createAsyncThunk(
  "user/verifyTrialSignupOtp",
  async ({ token, otp }, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/trial_signup/verify_otp";
    try {
      const response = await axios.post(
        url,
        { token, otp },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Failed to verify code" });
    }
  }
);

export const createNewUser = createAsyncThunk(
  "user/createNewUser",
  async ({ user, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users?account_id=${selectedAccount}`;

    try {
      const response = await axios.post(url, user, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const editUser = createAsyncThunk(
  "user/editUser",
  async ({ user, user_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${user_id}?account_id=${selectedAccount}`;

    try {
      const response = await axios.put(url, user, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const inviteNewUsers = createAsyncThunk(
  "user/inviteNewUsers",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = apiBaseUrl + `/users/${id}/send_invite?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const cancelInvite = createAsyncThunk(
  "user/cancelInvite",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = apiBaseUrl + `/users/${id}/remove_invite?account_id=${selectedAccount}`;
    try {
      const response = await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const acceptInvite = createAsyncThunk(
  "user/acceptInvite",
  async ({ id, selectedAccount }, { rejectWithValue }) => {
    const url = apiBaseUrl + `/users/${id}/accept_invite?account_id=${selectedAccount}`;
    try {
      const response = await axios.patch(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updatePassword = createAsyncThunk(
  "user/updatePassword",
  async ({ token, password, account_id }, { rejectWithValue }) => {
    const url = apiBaseUrl + "/users/update_password";
    try {
      const response = await axios.patch(
        url,
        { token, password, account_id },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUser = createAsyncThunk(
  "users/getUser",
  async ({ user_id, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${user_id}?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Archive User
export const archiveUser = createAsyncThunk(
  "users/archiveUser",
  async ({ userId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${userId}/archive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("User archived successfully");
      return { userId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive" });
    }
  }
);

// Unarchive User
export const unarchiveUser = createAsyncThunk(
  "users/unarchiveUser",
  async ({ userId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${userId}/unarchive?account_id=${selectedAccount}`;

    try {
      await axios.post(url, {}, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      toast.success("User unarchived successfully");
      return { userId };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive" });
    }
  }
);
export const getProfileUser = createAsyncThunk(
  "users/getProfileUser",
  async ({ id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${id}/get_profile_user`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const getUserStatus = createAsyncThunk(
  "users/getUserStatus",
  async ({ email }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/user_exists?email=${email}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// One group's workers, for the screens that already know which group they are
// asking about. Deliberately its own thunk and its own slot rather than a
// parameter on getRoleWiseUser: that one's payload is the whole company and
// eight screens read it, so narrowing it in place would hand them a truncated
// roster and no way to tell.
export const getGroupSubjects = createAsyncThunk(
  "users/getGroupSubjects",
  async ({ selectedAccount, groupId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/role_wise_users?account_id=${selectedAccount}&group_id=${groupId}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return { groupId, subjects: response.data?.data?.subjects ?? response.data?.subjects ?? [] };
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

export const getRoleWiseUser = createAsyncThunk(
  "users/getRoleWiseUser",
  async ({ selectedAccount, disableLoading = false }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/role_wise_users?account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
  {
    // A guest only reads the exams shared with them and has no use for the
    // account's roster, which the server refuses them anyway.
    condition: (_, { getState }) => getState().user.role !== "guest_user",
  }
);

export const getAllUsers = createAsyncThunk(
  "user/getAll",
  async (
    { searchValue, searchTags, selectedTag, page, rowsPerPage, role, selectedAccount, archive = false },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    const q = searchTags && selectedTag ? selectedTag.title : searchValue;
    const url =
      apiBaseUrl +
      `/users?q=${encodeURIComponent(q || "")}&tags=${!!searchTags}&page=${page + 1}&per_page=${rowsPerPage}&role=${role}&account_id=${selectedAccount}&archive=${archive}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Admin users list (uber admin only, global)
export const getAdminUsers = createAsyncThunk(
  "adminUsers/getAll",
  async (
    { searchValue, page, rowsPerPage, archive = false, pending = false, workers = false },
    { rejectWithValue }
  ) => {
    const bearerToken = getToken();
    const q = searchValue || "";
    const url =
      apiBaseUrl +
      `/admin/users?q=${encodeURIComponent(
        q
      )}&page=${page + 1}&per_page=${rowsPerPage}&archive=${archive}&pending=${pending}&workers=${workers}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Single admin user detail (uber admin only, global)
export const getAdminUserDetail = createAsyncThunk(
  "adminUsers/getOne",
  async ({ id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${id}`;

    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to fetch admin user" });
    }
  }
);

// Admin update user (info fields)
export const updateAdminUser = createAsyncThunk(
  "adminUsers/update",
  async ({ id, payload }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${id}`;

    try {
      const response = await axios.put(url, payload, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const createAdminUserCompany = createAsyncThunk(
  "adminUsers/createUserCompany",
  async ({ userId, payload }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${userId}/user_companies`;

    try {
      const response = await axios.post(url, payload, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to add role" });
    }
  }
);

export const updateAdminUserCompany = createAsyncThunk(
  "adminUsers/updateUserCompany",
  async ({ userId, userCompanyId, payload }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${userId}/user_companies/${userCompanyId}`;

    try {
      const response = await axios.put(url, payload, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to update role settings" });
    }
  }
);

export const deleteAdminUserCompany = createAsyncThunk(
  "adminUsers/deleteUserCompany",
  async ({ userId, userCompanyId }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${userId}/user_companies/${userCompanyId}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to remove role" });
    }
  }
);

// Admin archive user (all accounts)
export const archiveAdminUser = createAsyncThunk(
  "adminUsers/archive",
  async ({ id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${id}/archive`;

    try {
      const response = await axios.post(
        url,
        { archive: true },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success("User archived successfully");
      return { id, message: response.data?.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to archive user" });
    }
  }
);

// Admin unarchive user (all accounts)
export const unarchiveAdminUser = createAsyncThunk(
  "adminUsers/unarchive",
  async ({ id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users/${id}/archive`;

    try {
      const response = await axios.post(
        url,
        { archive: false },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      toast.success("User unarchived successfully");
      return { id, message: response.data?.message };
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to unarchive user" });
    }
  }
);

// Admin invite user (uber admin: invite to any account)
export const inviteAdminUser = createAsyncThunk(
  "adminUsers/invite",
  async ({ email, company_id, role_id }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/admin/users`;

    try {
      const response = await axios.post(
        url,
        { email, company_id, role_id },
        {
          headers: {
            "Content-Type": "application/json",
            "Access-Control-Allow-Origin": "*",
            Authorization: `Bearer ${bearerToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Invite failed" });
    }
  }
);


export const getPendingUsers = createAsyncThunk(
  "users/pendingUsers",
  async ({ searchValue, page, rowsPerPage, role, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = apiBaseUrl + `/users/pending_users?q${searchValue}&page=${page + 1}&per_page=${rowsPerPage}&role=${role}&account_id=${selectedAccount}`;
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
)

export const deleteUser = createAsyncThunk(
  "users/deleteUser",
  async ({ userId, selectedAccount }, { rejectWithValue }) => {
    const bearerToken = getToken();
    const url = `${apiBaseUrl}/users/${userId}?account_id=${selectedAccount}`;

    try {
      await axios.delete(url, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
          Authorization: `Bearer ${bearerToken}`,
        },
      });

      return { userId };
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const forgotPassword = createAsyncThunk(
  "user/forgotPassword",
  async (email, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/forgot_password";
    try {
      const response = await axios.post(url, { email }, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Add resetPassword thunk
export const changePassword = createAsyncThunk(
  "user/changePassword",
  async ({ token, password }, { rejectWithValue }) => {
    const url = apiBaseUrl + "/auth/reset_password";
    try {
      const response = await axios.put(url, { token, password }, {
        headers: {
          "Content-Type": "application/json",
          "Access-Control-Allow-Origin": "*",
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.selectedAccount = null;
      state.role = null
      localStorage.removeItem("user");
      localStorage.removeItem("selectedAccount");
      localStorage.removeItem("role");
    },
    selectAccount: (state, action) => {
      state.selectedAccount = action.payload;
      localStorage.setItem("selectedAccount", JSON.stringify(action.payload));
      const userRoles = JSON.parse(localStorage.getItem("user"))?.roles || [];
      // The fallback is hit when the user has no role for the account being
      // switched to. It used to hand back userRoles[0] whole, so state.role
      // became an object and anything calling role.split() threw.
      const matched = userRoles.find((r) => r?.account_id === action?.payload);
      const accountRole = normalizeRole(matched) ?? normalizeRole(userRoles[0]);
      localStorage.setItem("role", JSON.stringify(accountRole));
      state.role = accountRole;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.fulfilled, (state, action) => {
        state.user = action.payload;
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      .addCase(verifyTrialSignupOtp.fulfilled, (state, action) => {
        state.user = action.payload;
        localStorage.setItem("user", JSON.stringify(state.user));
      })
      .addCase(getUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.isLoading = false;
      })
      .addCase(getUserStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUserStatus.fulfilled, (state, action) => {
        state.userStatus = action.payload;
        state.isLoading = false;
      })
      .addCase(getRoleWiseUser.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getRoleWiseUser.pending, (state, action) => {
        const disableLoading = action.meta.arg?.disableLoading;
        state.isLoading = !disableLoading;
      })
      .addCase(getGroupSubjects.pending, (state) => {
        state.groupSubjectsLoading = true;
      })
      .addCase(getGroupSubjects.fulfilled, (state, action) => {
        state.groupSubjects = action.payload;
        state.groupSubjectsLoading = false;
      })
      .addCase(getGroupSubjects.rejected, (state) => {
        state.groupSubjects = null;
        state.groupSubjectsLoading = false;
      })
      .addCase(getRoleWiseUser.fulfilled, (state, action) => {
        state.roleWiseUser = action.payload;
        state.isLoading = false;
      })
      .addCase(getUser.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getAllUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.usersList = action.payload.users;
        state.isLoading = false;
        state.totalUsersCount = action.payload.meta.total_count;
      })
      .addCase(getAdminUserDetail.pending, (state) => {
        state.adminUserDetailLoading = true;
        state.adminUserDetailError = null;
      })
      .addCase(getAdminUserDetail.fulfilled, (state, action) => {
        state.adminUserDetail = action.payload;
        state.adminUserDetailLoading = false;
      })
      .addCase(getAdminUserDetail.rejected, (state, action) => {
        state.adminUserDetailLoading = false;
        state.adminUserDetailError = action.payload?.error || action.payload;
      })
      .addCase(getAdminUsers.pending, (state) => {
        state.adminUsersLoading = true;
      })
      .addCase(getAdminUsers.fulfilled, (state, action) => {
        state.adminUsersList = action.payload.users;
        state.adminUsersMeta = action.payload.meta;
        if (action.payload.invite_roles) {
          state.adminInviteRoles = action.payload.invite_roles;
        }
        state.adminPendingInvites = action.payload.pending_invites || [];
        state.adminUsersLoading = false;
      })
      .addCase(getAdminUsers.rejected, (state) => {
        state.adminUsersLoading = false;
      })
      .addCase(inviteAdminUser.fulfilled, (state) => {
        toast.success("User invited successfully");
      })
      .addCase(inviteAdminUser.rejected, (state, action) => {
        const msg = action.payload?.message;
        const errs = Array.isArray(msg) ? msg.join(", ") : msg;
        toast.error(errs || "Failed to invite user");
      })
      .addCase(archiveAdminUser.fulfilled, (state, action) => {
        const archivedId = action.payload?.id;
        if (archivedId) {
          state.adminUsersList = state.adminUsersList.filter((u) => u.id !== archivedId);
          if (state.adminUsersMeta && typeof state.adminUsersMeta.total_count === "number") {
            state.adminUsersMeta.total_count = Math.max(
              0,
              state.adminUsersMeta.total_count - 1
            );
          }
        }
      })
      .addCase(unarchiveAdminUser.fulfilled, (state, action) => {
        // No-op for list; reload handled by caller if needed
      })
      .addCase(getPendingUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPendingUsers.fulfilled, (state, action) => {
        state.pendingUsersList = action.payload.users;
        state.isLoading = false;
        state.totalPendingUsersCount = action.payload.meta.total_count;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.usersList = state.usersList.filter(
          (user) => user.id !== action.payload.userId
        );
      })
      .addCase(archiveUser.fulfilled, (state, action) => {
        state.usersList = state.usersList.filter(u => u.id !== action.payload.userId);
      })
      .addCase(unarchiveUser.fulfilled, (state, action) => {
        state.usersList = state.usersList.filter(u => u.id !== action.payload.userId);
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        toast.success("Password reset successfully");
      })
      .addCase(updatePassword.rejected, (state, action) => {
        toast.error(action.payload.error.join(', '));
      })
      .addCase(createNewUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createNewUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingUsersList.push(action.payload.user);
        state.totalPendingUsersCount += 1;
      })
      .addCase(createNewUser.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(cancelInvite.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(cancelInvite.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingUsersList = state.pendingUsersList.filter(
          (user) => user.id !== action.payload.user_id
        );
        state.totalPendingUsersCount -= 1;
      })
      .addCase(cancelInvite.rejected, (state, action) => {
        state.isLoading = false;
      })
      .addCase(forgotPassword.fulfilled, (state, action) => {
        toast.success("Password reset email sent");
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        toast.error(action.payload.message || "Failed to send reset email");
      })
      .addCase(sendTrialSignupOtp.fulfilled, (state, action) => {
        toast.success(action.payload?.message || "Verification code sent");
      })
      .addCase(sendTrialSignupOtp.rejected, () => {})
      .addCase(verifyTrialSignupOtp.rejected, (state, action) => {
        const errors = action.payload?.errors;
        const message =
          action.payload?.message ||
          (errors ? Object.values(errors).flat().join(", ") : null) ||
          "Failed to verify code";
        toast.error(message);
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        toast.success("Password reset successfully");
      })
      .addCase(changePassword.rejected, (state, action) => {
        toast.error(action.payload.message || "Failed to reset password");
      })
      .addCase(updateAdminUser.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === "object" && !Array.isArray(action.payload)) {
          state.adminUserDetail = action.payload;
        }
        toast.success("User updated successfully");
      })
      .addCase(createAdminUserCompany.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === "object" && !Array.isArray(action.payload)) {
          state.adminUserDetail = action.payload;
        }
        toast.success("Role added successfully");
      })
      .addCase(updateAdminUserCompany.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === "object" && !Array.isArray(action.payload)) {
          state.adminUserDetail = action.payload;
        }
        toast.success("Role settings updated successfully");
      })
      .addCase(deleteAdminUserCompany.fulfilled, (state, action) => {
        if (action.payload && typeof action.payload === "object" && !Array.isArray(action.payload)) {
          state.adminUserDetail = action.payload;
        }
        toast.success("Role removed successfully");
      })
      .addCase(updateAdminUser.rejected, (state, action) => {
        toast.error(
          action.payload?.error ||
            action.payload?.errors ||
            "Failed to update user"
        );
      })
      .addCase(createAdminUserCompany.rejected, (state, action) => {
        toast.error(action.payload?.error || action.payload?.errors || "Failed to add role");
      })
      .addCase(updateAdminUserCompany.rejected, (state, action) => {
        toast.error(action.payload?.error || action.payload?.errors || "Failed to update role settings");
      })
      .addCase(deleteAdminUserCompany.rejected, (state, action) => {
        toast.error(action.payload?.error || action.payload?.errors || "Failed to remove role");
      })
      .addCase(getProfileUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfileUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.profileUser = action.payload;
      })
      .addCase(getProfileUser.rejected, (state, action) => {
        state.isLoading = false;
        toast.error(action.payload?.message || "Failed to fetch user profile");
      });
  },
});

export const { logout, selectAccount } = userSlice.actions;
export default userSlice.reducer;
